import axios from 'axios';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { IoIosRepeat } from 'react-icons/io';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { PageTitle } from '../../../../_metronic/layout/core';
import DateTimeComp from '../../../../components/dateComponent/DateTimeComp';
import { LoadingComponent } from '../../../../components/loadingComponent/LoadingComponent';
import NoRecordFound from '../../../../components/noRecord/NoRecordFound';
import Pagination from '../../../../components/pagination/Pagination';
import SearchBarComponent from '../../../../components/searchBar/SearchBarComponent';
import TableHeading from '../../../../components/tableHeading/TableHeading';
import TableSettingMenu from '../../../../components/tableHeading/TableSettingMenu';
import TextField from '../../../../components/textFields/TextField';
import { selectAuth } from '../../../../features/authSlice';
import { useAppSelector } from '../../../../hooks';
import { API_END_POINTS } from '../../../../services/apiEndpoints';
import { useGetAutomationBankFilesFeedParsingStatsQuery } from '../../../../services/AutomationApi';
import labelKey from '../../../localization/label.json';
import tosterKey from "../../../localization/toster.json";
import BankCCFeedDetailViewModal from './BankCCFeedDetailViewModal';
import RefreshComponent from '../../../../components/refreshComponent/RefreshComponent';

interface BankCCFeedsProps {
  removeRoundedTop?: boolean;
  showImportButtons?:boolean
}

interface ColumnConfig {
  id: string;
  label: string;
  sortable?: boolean;
  className?: string;
  render?: (data: any) => React.ReactNode;
}

const BankCCFeeds = ({ removeRoundedTop = false, showImportButtons = false }: BankCCFeedsProps) => {
  const { token, microServiceURL } = useAppSelector(selectAuth);
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(100);
  const [searchStr, setSearchStr] = useState('');
  const [immediateSearchStr, setImmediateSearchStr] = useState('');
  const [sortBy, setSortBy] = useState("createDT");
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortedColumn, setSortedColumn] = useState("createDT");
  const [filterByStatus, setFilterByStatus] = useState("");

  const { data, isFetching: isLoading, isSuccess, refetch } = useGetAutomationBankFilesFeedParsingStatsQuery({
    pageNumber: pageNumber,
    pageSize: pageSize,
    searchStr: searchStr,
    sortBy: sortBy,
    sortOrder: sortOrder,
    filterByStatus: filterByStatus
  })

  const handlePageSizeChange = (e: any) => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setPageNumber(1);
  };

  const handlePageClick = (e: any) => {
    setPageNumber(e.selected + 1);
  };

  const handlePageRecords = (e: any) => {
    setPageNumber(1);
  };

  const handleSort = (property: string) => {
    setSortBy(property);
    if (sortedColumn === property) {
      setSortOrder(prevSortOrder => (prevSortOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortedColumn(property);
      setSortOrder("asc");
    }
  };

  const handleSearchChange = (e: any) => {
    const { value } = e.target;
    setImmediateSearchStr(value);
    delayedSearch(value);
    setPageNumber(1)
  };

  const delayedSearch = useCallback(
    debounce((searchValue) => {
      setSearchStr(searchValue);
    }, 500),
    []
  );

  useEffect(() => {
    if (searchStr !== "" && sortOrder !== "" && sortBy !== "") {
      refetch();
    }
  }, [searchStr, sortOrder, sortBy, refetch]);

  const columns: ColumnConfig[] = [
    { id: 'sr', label: labelKey.sr, className: "w-60px ps-2" },
    { id: 'fileName', label: labelKey.fileName, sortable: true, className: "w-400px text-center" },
    { id: 'parsingStatus', label: labelKey.importStatus, sortable: true, className: "w-400px text-center"  },
    { id: 'parsingStartTime', label: labelKey.parsingStartDate, sortable: true, className: "w-400px text-center"  },
    { id: 'parsingEndTime', label: labelKey.parsingEndDate, sortable: true, className: "w-400px text-center"  },
    { id: 'totalCountLineItemDetailEntity', label: labelKey.feedItemsCount, sortable: true, className: "w-100px" },
    { id: 'reconcileFieldsItemCount', label: "Reconcile Fields Item Count", sortable: true, className: "w-150px" },
    { id: 'isReconcileCompleted', label: "Is Reconcile Completed", sortable: true, className: "w-150px" },
    { id: 'totalCountLineItemDetailEntityParsed', label: labelKey.feedItemsParsedCount, sortable: true, className: "w-100px" },
    { id: 'totalCountCCLineItemsToSSExpItemsCreated', label: labelKey.linkedLineItemsCount, sortable: true, className: "w-100px" },
    { id: 'modifiedDate', label: "Modified Date", sortable: true, className: "w-180px text-center" },
    { id: 'createdDT', label: "Created Date", sortable: true, className: "w-180px text-center" },
    { id: 'actions', label: labelKey.actions }
  ];

  const defaultHiddenColumns = ['sr', 'fileName', 'totalAmountOfDebits', 'totalParsedAmount', 'totalCountLineItemDetailEntity', 'totalCountLineItemDetailEntityParsed', 'totalCountCCLineItemsToSSExpItemsCreated', 'totalLineItemCreatedAmount', 'parsingStatus', 'parsingStartTime', 'parsingEndTime', 'isReconcileCompleted', 'reconcileFieldsItemCount', 'modifiedDate', 'createdDT', 'actions'];
  
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(defaultHiddenColumns);

  const handleToggleColumn = (columnId: string) => {
    if (hiddenColumns.includes(columnId)) {
      setHiddenColumns(hiddenColumns.filter((id) => id !== columnId));
    } else {
      setHiddenColumns([...hiddenColumns, columnId]);
    }
  };

  const renderCellContent = (column: ColumnConfig, dataItem: any, index: number) => {
    switch(column.id) {
      case 'sr':
        return (pageNumber - 1) * pageSize + index + 1;
      case 'fileName':
        return dataItem?.fileName;
      case 'parsingStatus':
        return dataItem?.parsingStatus === true ? 
          <span className="badge badge-light-success fs-7 fw-semibold">{labelKey.parsed}</span> :
          <span className="badge badge-light-danger fs-7 fw-semibold">{labelKey.failed}</span>;
      case 'parsingStartTime':
        return <DateTimeComp formattedDate={dataItem?.parsingStartTime} />;
      case 'parsingEndTime':
        return <DateTimeComp formattedDate={dataItem?.parsingEndTime} />;
      case 'totalCountLineItemDetailEntity':
        return dataItem?.totalCountLineItemDetailEntity ? dataItem?.totalCountLineItemDetailEntity : "N/A";
      case 'totalCountLineItemDetailEntityParsed':
        return dataItem?.totalCountLineItemDetailEntityParsed ? dataItem?.totalCountLineItemDetailEntityParsed : "N/A";
      case 'totalCountCCLineItemsToSSExpItemsCreated':
        return dataItem?.totalCountCCLineItemsToSSExpItemsCreated ? dataItem?.totalCountCCLineItemsToSSExpItemsCreated : "N/A";
      case 'isReconcileCompleted':
        return dataItem?.isReconcileCompleted === true ? 
          <span className="badge badge-light-success fs-7 fw-semibold">Yes</span> :
          <span className="badge badge-light-danger fs-7 fw-semibold">No</span>;
      case 'reconcileFieldsItemCount':
        return dataItem?.reconcileFieldsItemCount ? dataItem?.reconcileFieldsItemCount : "N/A";
      case 'modifiedDate':
        return <DateTimeComp formattedDate={dataItem?.modifiedDate} />;
      case 'createdDT':
        return <DateTimeComp formattedDate={dataItem?.createdDT} />;
      case 'actions':
        return (
          <p className='text-end pe-9 text-primary mb-0'
            onClick={() => {
              setBankFeedData(dataItem);
              bankFeedDetailHandler();
            }}>
            View Detail
          </p>
        );
      default:
        return null;
    }
  };

  const getCellClassName = (column: ColumnConfig) => {
    if (['totalCountLineItemDetailEntity', 'totalCountLineItemDetailEntityParsed', 'totalCountCCLineItemsToSSExpItemsCreated', 'reconcileFieldsItemCount'].includes(column.id)) {
      return 'text-end';
    }
    return 'text-center';
  };

  const [showBankFeedDetailModal, setShowBankFeedDetailModal] = useState(false)
  const [bankFeedData, setBankFeedData] = useState();

  const bankFeedDetailHandler = () => {
    setShowBankFeedDetailModal((prev) => !prev);
  };

  useEffect(() => {
    if (showBankFeedDetailModal === false) {
      refetch()
    }
  }, [showBankFeedDetailModal])

  const [creatingFileIsLoading, setCreatingFileIsLoading] = useState(false);
  const importCCFilesHandler = async () => {
    setCreatingFileIsLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      const getCreatingFileResponse = await axios.post(
        `${microServiceURL}/api/BackgroundJob${API_END_POINTS.automationCreditCardFeedsFilesManualCall}`,
        {
          headers,
        }
      );
      refetch()
      toast.success('CC Files imported successfully!');
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setCreatingFileIsLoading(false);
    }
  }

  const [creatingLineItemIsLoading, setCreatingLineItemIsLoading] = useState(false);
  const creatingLineItemHandler = async () => {
    setCreatingLineItemIsLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      const getCCLineItemResponse = await axios.post(
        `${microServiceURL}/api/BackgroundJob${API_END_POINTS.automationCreaditCardFeedsToLineItemManualCall}`,
        {
          headers,
        }
      );
      refetch()
      toast.success('CC Line Item Created successfully!');
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setCreatingLineItemIsLoading(false);
    }
  }

  const [creatingReportIsLoading, setCreatingReportIsLoading] = useState(false);
  const creatingReportHandler = async () => {
    setCreatingReportIsLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      const getCCCreateReportResponse = await axios.post(
        `${microServiceURL}/api/BackgroundJob${API_END_POINTS.automationCreaditCardFeedsToCreateExpReportManualCall}`,
        {
          headers,
        }
      );
      refetch()
      toast.success('CC Report Created successfully!');
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setCreatingReportIsLoading(false);
    }
  }

  const handleRefresh = () => {
    refetch()
      .then(() => {
        if (isSuccess) {
          toast.success(tosterKey.dataRefreshedSuccesfully);
        }
      });
  };

  const visibleColumns = columns.filter(col => hiddenColumns.includes(col.id));

  return (
    <>
      <BankCCFeedDetailViewModal
        show={showBankFeedDetailModal}
        handleClose={() => setShowBankFeedDetailModal(false)}
        bankFeedData={bankFeedData}
      />
      <PageTitle breadcrumbs={[]}>{labelKey.automationBankCCFeeds}</PageTitle>

      <div className={`card mb-5 ${removeRoundedTop ? 'rounded-top-0' : ''}`}>
        <div className='card-header border-0 pt-6'>
          <div className='d-flex align-items-center '>
            <SearchBarComponent
              placeholder='File Parsing'
              value={immediateSearchStr}
              onChange={handleSearchChange}
            />
            <Tooltip id="refreshData" place="top" />
            <RefreshComponent onClick={handleRefresh} />
          </div>
          {showImportButtons === false && (
            <>
               <div className='d-flex align-items-center ms-auto'>
            <div className='card-toolbar gap-3'>
              <button className='btn btn-sm btn-primary'
                onClick={importCCFilesHandler}
                disabled={creatingFileIsLoading} >
                {creatingFileIsLoading ? 'Loading...' : labelKey.importCCFiles}
              </button>
              <button className='btn btn-sm btn-primary'
                onClick={creatingLineItemHandler}
                disabled={creatingLineItemIsLoading} >
                {creatingLineItemIsLoading ? 'Loading...' : labelKey.linkedCCLineItem}
              </button>
              <button className='btn btn-sm btn-primary'
                onClick={creatingReportHandler}
                disabled={creatingReportIsLoading} >
                {creatingReportIsLoading ? 'Loading...' : labelKey.createCCReports}
              </button>
            </div>
          </div>
            </>
          )}
       
        </div>
        <div className='card-body py-8'>
          <div className="col-md-12">
            <div className='table-responsive table-height'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    {visibleColumns.map((column) => (
                      column.id === 'actions' ? (
                        <th key={column.id} className='text-end rounded-end pe-2 border-0'>
                          {labelKey.actions}
                          <TableSettingMenu>
                            {columns.filter(col => col.id !== 'actions').map((col) => (
                              <TextField
                                key={col.id}
                                rightLabel={col.label}
                                type="checkbox"
                                onChange={() => handleToggleColumn(col.id)}
                                checked={hiddenColumns.includes(col.id)}
                                fieldClass='mb-4'
                              />
                            ))}
                          </TableSettingMenu>
                        </th>
                      ) : (
                        <TableHeading
                          key={column.id}
                          label={column.label}
                          sortedColumn={sortedColumn}
                          sortOrder={sortOrder}
                          handleSort={column.sortable ? handleSort : undefined}
                          columnId={column.id}
                          className={column.className || ''}
                        />
                      )
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!isLoading &&
                    <>
                      {data?.result?.data?.length > 0 ? (
                        <>
                          {data?.result?.data?.map((dataItem: any, index: any) => (
                            <tr key={index}>
                              {visibleColumns.map((column) => (
                                <td key={column.id} className={getCellClassName(column)}>
                                  {renderCellContent(column, dataItem, index)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </>) : (
                        <tr>
                          <td colSpan={visibleColumns.length}>
                            <NoRecordFound />
                          </td>
                        </tr>
                      )}
                    </>}
                  {isLoading && (
                    <tr>
                      <td colSpan={visibleColumns.length} className="text-center">
                        <LoadingComponent />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              totalResult={data?.result?.totalRecords === 0 ? 0 : ((data?.result?.pageNumber - 1) * data?.result?.pageSize) + 1}
              toResult={data?.result?.totalRecords === 0 ? 0 : Math.min(data?.result?.pageNumber * data?.result?.pageSize, data?.result?.totalRecords)}
              ofResult={data?.result?.totalRecords}
              onChange={handlePageSizeChange}
              pageSize={pageSize}
              onPageChange={handlePageClick}
              pageCount={data?.result?.totalPages || 0}
              currentPage={pageNumber}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default BankCCFeeds